import React, { useState } from 'react';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDate } from '../utils/dateFormatter';
import { TruckIcon, PlusIcon, SearchIcon, FilterIcon, ArrowDownIcon, ArrowUpIcon, EyeIcon, CalendarIcon, UserIcon, ClipboardListIcon, XIcon, FileTextIcon, PackageIcon, ShoppingCartIcon, CheckCircleIcon, AlertTriangleIcon, ClockIcon, LinkIcon } from 'lucide-react';
export function ReceiptList({
  onCreateReceipt,
  onViewReceiptDetails,
  locale = 'en-US'
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  // Sample data - in a real app this would come from your backend
  const sampleReceipts = [{
    id: 'GR-2023-001',
    date: '2023-11-05',
    receivedBy: 'John Smith',
    status: 'Complete',
    totalValue: 3000.0,
    currency: 'USD',
    purchaseOrders: [{
      poId: 'PO-2023-001',
      poName: 'Server Hardware',
      vendor: 'Server Solutions Inc.',
      items: [{
        id: 'ITEM-001',
        description: 'Dell XPS 15 Laptop',
        itemCode: 'LPT-001',
        quantity: 2,
        uom: 'EA',
        unitPrice: 1500.0,
        value: 3000.0,
        poQuantity: 5,
        remainingQuantity: 3,
        notes: 'Items received in good condition'
      }]
    }],
    deliveryDockets: [{
      id: 'DD-001-001',
      name: 'Delivery_Note_12345.pdf',
      uploadDate: '2023-11-05',
      uploadedBy: 'John Smith'
    }],
    notes: 'Partial delivery of laptops. Remaining units expected next week.',
    entity: 'Corporate',
    site: 'Headquarters',
    deliveryAddress: '123 Corporate Drive, Business Park, CA 94105',
    carrierInfo: 'FedEx',
    trackingNumber: '7891234567',
    inspectedBy: 'Alice Johnson',
    qualityCheck: 'Passed',
    inspectionDate: '2023-11-05'
  }, {
    id: 'GR-2023-002',
    date: '2023-11-10',
    receivedBy: 'Jane Doe',
    status: 'Partial',
    totalValue: 1250.0,
    currency: 'USD',
    purchaseOrders: [{
      poId: 'PO-2023-001',
      poName: 'Server Hardware',
      vendor: 'Server Solutions Inc.',
      items: [{
        id: 'ITEM-002',
        description: 'IT Setup and Configuration Services',
        itemCode: 'SVC-001',
        quantity: 0.5,
        uom: 'LOT',
        unitPrice: 2500.0,
        value: 1250.0,
        poQuantity: 1,
        remainingQuantity: 0.5,
        notes: 'Initial setup completed, final configuration pending'
      }]
    }],
    deliveryDockets: [],
    notes: 'Service partially completed. Final configuration scheduled for next month.',
    entity: 'Corporate',
    site: 'Headquarters',
    deliveryAddress: '123 Corporate Drive, Business Park, CA 94105',
    carrierInfo: 'N/A - Service',
    trackingNumber: 'N/A',
    inspectedBy: 'Robert Chen',
    qualityCheck: 'In Progress',
    inspectionDate: '2023-11-10'
  }, {
    id: 'GR-2023-003',
    date: '2023-10-10',
    receivedBy: 'Mark Johnson',
    status: 'Partial',
    totalValue: 750.0,
    currency: 'USD',
    purchaseOrders: [{
      poId: 'PO-2023-002',
      poName: 'Marketing Materials',
      vendor: 'PrintWorks Inc.',
      items: [{
        id: 'ITEM-003',
        description: 'Company Brochures',
        itemCode: 'BRO-001',
        quantity: 500,
        uom: 'EA',
        unitPrice: 1.5,
        value: 750.0,
        poQuantity: 1000,
        remainingQuantity: 500,
        notes: 'First batch of brochures received'
      }]
    }],
    deliveryDockets: [{
      id: 'DD-003-001',
      name: 'PrintWorks_Delivery_56789.pdf',
      uploadDate: '2023-10-10',
      uploadedBy: 'Mark Johnson'
    }],
    notes: 'First batch of marketing materials. Quality is excellent.',
    entity: 'Marketing',
    site: 'Headquarters',
    deliveryAddress: '123 Corporate Drive, Business Park, CA 94105',
    carrierInfo: 'UPS',
    trackingNumber: '1234567890',
    inspectedBy: 'Sarah Williams',
    qualityCheck: 'Passed',
    inspectionDate: '2023-10-10'
  }, {
    id: 'GR-2023-004',
    date: '2023-10-12',
    receivedBy: 'Mike Johnson',
    status: 'Complete',
    totalValue: 3950.5,
    currency: 'USD',
    purchaseOrders: [{
      poId: 'PO-2023-002',
      poName: 'Marketing Materials',
      vendor: 'PrintWorks Inc.',
      items: [{
        id: 'ITEM-004',
        description: 'Company Brochures',
        itemCode: 'BRO-001',
        quantity: 500,
        uom: 'EA',
        unitPrice: 1.5,
        value: 750.0,
        poQuantity: 1000,
        remainingQuantity: 0,
        notes: 'Final batch of brochures received'
      }, {
        id: 'ITEM-005',
        description: 'Banners',
        itemCode: 'BAN-001',
        quantity: 5,
        uom: 'EA',
        unitPrice: 340.1,
        value: 1700.5,
        poQuantity: 5,
        remainingQuantity: 0,
        notes: 'All banners received in good condition'
      }]
    }, {
      poId: 'PO-2023-005',
      poName: 'Trade Show Materials',
      vendor: 'PrintWorks Inc.',
      items: [{
        id: 'ITEM-006',
        description: 'Display Stands',
        itemCode: 'DSP-001',
        quantity: 3,
        uom: 'EA',
        unitPrice: 500.0,
        value: 1500.0,
        poQuantity: 3,
        remainingQuantity: 0,
        notes: 'Display stands arrived in good condition'
      }]
    }],
    deliveryDockets: [{
      id: 'DD-004-001',
      name: 'PrintWorks_Delivery_56790.pdf',
      uploadDate: '2023-10-12',
      uploadedBy: 'Mike Johnson'
    }, {
      id: 'DD-004-002',
      name: 'PrintWorks_TradeShow_Delivery_78901.pdf',
      uploadDate: '2023-10-12',
      uploadedBy: 'Mike Johnson'
    }],
    notes: 'Combined delivery of marketing materials and trade show equipment from PrintWorks. All items received in good condition.',
    entity: 'Marketing',
    site: 'Headquarters',
    deliveryAddress: '123 Corporate Drive, Business Park, CA 94105',
    carrierInfo: 'UPS',
    trackingNumber: '0987654321',
    inspectedBy: 'Sarah Williams',
    qualityCheck: 'Passed',
    inspectionDate: '2023-10-12'
  }, {
    id: 'GR-2023-005',
    date: '2023-10-15',
    receivedBy: 'Robert Chen',
    status: 'Complete',
    totalValue: 4750.0,
    currency: 'USD',
    purchaseOrders: [{
      poId: 'PO-2023-007',
      poName: 'Office Computers',
      vendor: 'Tech Solutions Inc.',
      items: [{
        id: 'ITEM-007',
        description: 'Desktop Computer',
        itemCode: 'DTC-001',
        quantity: 2,
        uom: 'EA',
        unitPrice: 1200.0,
        value: 2400.0,
        poQuantity: 2,
        remainingQuantity: 0,
        notes: 'Computers arrived in perfect condition'
      }]
    }, {
      poId: 'PO-2023-008',
      poName: 'Office Peripherals',
      vendor: 'Tech Solutions Inc.',
      items: [{
        id: 'ITEM-008',
        description: 'Monitor 27"',
        itemCode: 'MON-001',
        quantity: 3,
        uom: 'EA',
        unitPrice: 350.0,
        value: 1050.0,
        poQuantity: 3,
        remainingQuantity: 0,
        notes: 'All monitors in good condition'
      }, {
        id: 'ITEM-009',
        description: 'Keyboard and Mouse Set',
        itemCode: 'KMS-001',
        quantity: 5,
        uom: 'SET',
        unitPrice: 60.0,
        value: 300.0,
        poQuantity: 5,
        remainingQuantity: 0,
        notes: 'All peripherals working correctly'
      }]
    }, {
      poId: 'PO-2023-009',
      poName: 'Office Network Equipment',
      vendor: 'Tech Solutions Inc.',
      items: [{
        id: 'ITEM-010',
        description: 'Network Switch',
        itemCode: 'NSW-001',
        quantity: 1,
        uom: 'EA',
        unitPrice: 1000.0,
        value: 1000.0,
        poQuantity: 1,
        remainingQuantity: 0,
        notes: 'Network switch installed and tested'
      }]
    }],
    deliveryDockets: [{
      id: 'DD-005-001',
      name: 'TechSolutions_Delivery_12345.pdf',
      uploadDate: '2023-10-15',
      uploadedBy: 'Robert Chen'
    }],
    notes: 'Complete delivery of all IT equipment from Tech Solutions Inc. All items tested and working correctly.',
    entity: 'IT Department',
    site: 'Headquarters',
    deliveryAddress: '123 Corporate Drive, Business Park, CA 94105',
    carrierInfo: 'DHL',
    trackingNumber: '123456789012',
    inspectedBy: 'Robert Chen',
    qualityCheck: 'Passed',
    inspectionDate: '2023-10-15'
  }];
  // Get primary vendor for display in the table
  const getPrimaryVendor = receipt => {
    if (!receipt.purchaseOrders || receipt.purchaseOrders.length === 0) {
      return 'N/A';
    }
    return receipt.purchaseOrders[0].vendor;
  };
  // Get primary PO for display in the table
  const getPrimaryPO = receipt => {
    if (!receipt.purchaseOrders || receipt.purchaseOrders.length === 0) {
      return 'N/A';
    }
    return receipt.purchaseOrders[0].poId;
  };
  // Get all items from all POs
  const getAllItems = receipt => {
    if (!receipt.purchaseOrders) return [];
    let allItems = [];
    receipt.purchaseOrders.forEach(po => {
      if (po.items) {
        allItems = [...allItems, ...po.items.map(item => ({
          ...item,
          poId: po.poId,
          poName: po.poName,
          vendor: po.vendor
        }))];
      }
    });
    return allItems;
  };
  // Filter receipts based on search query and status filter
  const filteredReceipts = sampleReceipts.filter(receipt => {
    const matchesSearch = receipt.id.toLowerCase().includes(searchQuery.toLowerCase()) || receipt.purchaseOrders && receipt.purchaseOrders.some(po => po.poId.toLowerCase().includes(searchQuery.toLowerCase()) || po.poName.toLowerCase().includes(searchQuery.toLowerCase()) || po.vendor.toLowerCase().includes(searchQuery.toLowerCase()));
    if (filterStatus === 'all') {
      return matchesSearch;
    }
    return matchesSearch && receipt.status.toLowerCase() === filterStatus.toLowerCase();
  });
  // Sort receipts
  const sortedReceipts = [...filteredReceipts].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    // Handle special case for date
    if (sortField === 'date') {
      aValue = new Date(a.date).getTime();
      bValue = new Date(b.date).getTime();
    }
    // Handle sorting by vendor or PO
    else if (sortField === 'vendor') {
      aValue = getPrimaryVendor(a);
      bValue = getPrimaryVendor(b);
    } else if (sortField === 'poId') {
      aValue = getPrimaryPO(a);
      bValue = getPrimaryPO(b);
    }
    if (aValue < bValue) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });
  // Handle sort click
  const handleSortClick = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Render sort indicator
  const renderSortIndicator = field => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 ml-1" data-id="element-14629" /> : <ArrowDownIcon className="h-4 w-4 ml-1" data-id="element-14630" />;
  };
  // Handle view receipt details
  const handleViewReceipt = receipt => {
    setSelectedReceipt(receipt);
    setShowDetailsModal(true);
    // Comment out the parent handler call to prevent the alert
    // This was causing the error when clicking View
    // if (onViewReceiptDetails) {
    //   onViewReceiptDetails(receipt)
    // }
  };
  // Close the details modal
  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedReceipt(null);
  };
  return <div className="space-y-6" data-id="element-14631">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-14632">
        <div className="w-full sm:w-96" data-id="element-14633">
          <div className="relative rounded-md shadow-sm" data-id="element-14634">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-14635">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-14636" />
            </div>
            <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search by GR#, PO#, or vendor" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-14637" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto" data-id="element-14638">
          <div className="relative inline-block text-left" data-id="element-14639">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14640">
              <option value="all" data-id="element-14641">All Statuses</option>
              <option value="complete" data-id="element-14642">Complete</option>
              <option value="partial" data-id="element-14643">Partial</option>
            </select>
          </div>
          <button onClick={() => onCreateReceipt()} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14644">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-14645" />
            Create New Receipt
          </button>
        </div>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-14646">
        <div className="overflow-x-auto" data-id="element-14647">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-14648">
            <thead className="bg-gray-50" data-id="element-14649">
              <tr data-id="element-14650">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('id')} data-id="element-14651">
                  <div className="flex items-center" data-id="element-14652">
                    Receipt # {renderSortIndicator('id')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('poId')} data-id="element-14653">
                  <div className="flex items-center" data-id="element-14654">
                    PO # {renderSortIndicator('poId')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('vendor')} data-id="element-14655">
                  <div className="flex items-center" data-id="element-14656">
                    Vendor {renderSortIndicator('vendor')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('date')} data-id="element-14657">
                  <div className="flex items-center" data-id="element-14658">
                    Date {renderSortIndicator('date')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('receivedBy')} data-id="element-14659">
                  <div className="flex items-center" data-id="element-14660">
                    Received By {renderSortIndicator('receivedBy')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('status')} data-id="element-14661">
                  <div className="flex items-center" data-id="element-14662">
                    Status {renderSortIndicator('status')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('totalValue')} data-id="element-14663">
                  <div className="flex items-center" data-id="element-14664">
                    Value {renderSortIndicator('totalValue')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14665">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-14666">
              {sortedReceipts.length > 0 ? sortedReceipts.map(receipt => <tr key={receipt.id} className="hover:bg-gray-50" data-id="element-14667">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-14668">
                      {receipt.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14669">
                      <div className="flex flex-col" data-id="element-14670">
                        <div className="flex items-center" data-id="element-14671">
                          {getPrimaryPO(receipt)}
                        </div>
                        {receipt.purchaseOrders && receipt.purchaseOrders.length > 1 && <div className="text-xs text-gray-400 mt-1 flex items-center" data-id="element-14672">
                              <LinkIcon className="h-3 w-3 mr-1" data-id="element-14673" />
                              {receipt.purchaseOrders.length} POs
                            </div>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14674">
                      <div className="flex flex-col" data-id="element-14675">
                        <div className="flex items-center" data-id="element-14676">
                          {getPrimaryVendor(receipt)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14677">
                      <div className="flex items-center" data-id="element-14678">
                        <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-14679" />
                        {formatDate(receipt.date, locale)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14680">
                      <div className="flex items-center" data-id="element-14681">
                        <UserIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-14682" />
                        {receipt.receivedBy}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-14683">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${receipt.status === 'Complete' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-14684">
                        {receipt.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14685">
                      {formatCurrency(receipt.totalValue, receipt.currency, locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-14686">
                      <button onClick={() => handleViewReceipt(receipt)} className="text-indigo-600 hover:text-indigo-900 inline-flex items-center" data-id="element-14687">
                        <EyeIcon className="h-4 w-4 mr-1" data-id="element-14688" />
                        View
                      </button>
                    </td>
                  </tr>) : <tr data-id="element-14689">
                  <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-14690">
                    No receipts found
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex items-center justify-between mt-4 text-sm text-gray-600" data-id="element-14691">
        <div data-id="element-14692">
          Showing {sortedReceipts.length} of {sampleReceipts.length} receipts
        </div>
      </div>
      {/* Receipt Details Modal */}
      {showDetailsModal && selectedReceipt && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-14693">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-14694">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-14695">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-14696"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-14697">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" data-id="element-14698">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-14699">
                <div className="flex justify-between items-start pb-3 border-b border-gray-200" data-id="element-14700">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-14701">
                    <TruckIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-14702" />
                    Goods Receipt Details - {selectedReceipt.id}
                  </h3>
                  <button onClick={closeDetailsModal} className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-14703">
                    <XIcon className="h-6 w-6" data-id="element-14704" />
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-14705">
                  {/* Left Column - Receipt Details */}
                  <div data-id="element-14706">
                    <div className="bg-gray-50 p-4 rounded-md mb-4" data-id="element-14707">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-14708">
                        <FileTextIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-14709" />
                        Receipt Information
                      </h4>
                      <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-14710">
                        <div data-id="element-14711">
                          <p className="text-gray-500" data-id="element-14712">Receipt Date:</p>
                          <p className="font-medium" data-id="element-14713">
                            {formatDate(selectedReceipt.date, locale)}
                          </p>
                        </div>
                        <div data-id="element-14714">
                          <p className="text-gray-500" data-id="element-14715">Received By:</p>
                          <p className="font-medium" data-id="element-14716">
                            {selectedReceipt.receivedBy}
                          </p>
                        </div>
                        <div data-id="element-14717">
                          <p className="text-gray-500" data-id="element-14718">Status:</p>
                          <p className="font-medium" data-id="element-14719">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedReceipt.status === 'Complete' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-14720">
                              {selectedReceipt.status}
                            </span>
                          </p>
                        </div>
                        <div data-id="element-14721">
                          <p className="text-gray-500" data-id="element-14722">Entity/Site:</p>
                          <p className="font-medium" data-id="element-14723">
                            {selectedReceipt.entity} / {selectedReceipt.site}
                          </p>
                        </div>
                        <div data-id="element-14724">
                          <p className="text-gray-500" data-id="element-14725">Inspected By:</p>
                          <p className="font-medium" data-id="element-14726">
                            {selectedReceipt.inspectedBy}
                          </p>
                        </div>
                        <div data-id="element-14727">
                          <p className="text-gray-500" data-id="element-14728">Inspection Date:</p>
                          <p className="font-medium" data-id="element-14729">
                            {formatDate(selectedReceipt.inspectionDate, locale)}
                          </p>
                        </div>
                        <div data-id="element-14730">
                          <p className="text-gray-500" data-id="element-14731">Quality Check:</p>
                          <p className="font-medium" data-id="element-14732">
                            {selectedReceipt.qualityCheck}
                          </p>
                        </div>
                        <div data-id="element-14733">
                          <p className="text-gray-500" data-id="element-14734">PO Count:</p>
                          <p className="font-medium" data-id="element-14735">
                            {selectedReceipt.purchaseOrders ? selectedReceipt.purchaseOrders.length : 0}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md" data-id="element-14736">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-14737">
                        <ShoppingCartIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-14738" />
                        Purchase Order Information
                      </h4>
                      {selectedReceipt.purchaseOrders && selectedReceipt.purchaseOrders.map((po, index) => <div key={po.poId} className={`${index > 0 ? 'mt-3 pt-3 border-t border-gray-200' : ''}`} data-id="element-14739">
                            <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-14740">
                              <div data-id="element-14741">
                                <p className="text-gray-500" data-id="element-14742">PO Number:</p>
                                <p className="font-medium text-indigo-600" data-id="element-14743">
                                  {po.poId}
                                </p>
                              </div>
                              <div data-id="element-14744">
                                <p className="text-gray-500" data-id="element-14745">PO Name:</p>
                                <p className="font-medium" data-id="element-14746">{po.poName}</p>
                              </div>
                              <div data-id="element-14747">
                                <p className="text-gray-500" data-id="element-14748">Vendor:</p>
                                <p className="font-medium" data-id="element-14749">{po.vendor}</p>
                              </div>
                              <div data-id="element-14750">
                                <p className="text-gray-500" data-id="element-14751">Currency:</p>
                                <p className="font-medium" data-id="element-14752">
                                  {selectedReceipt.currency}
                                </p>
                              </div>
                            </div>
                          </div>)}
                      <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-14753">
                        <div className="text-sm" data-id="element-14754">
                          <p className="text-gray-500" data-id="element-14755">Delivery Address:</p>
                          <p className="font-medium" data-id="element-14756">
                            {selectedReceipt.deliveryAddress}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Right Column - Shipping and Notes */}
                  <div data-id="element-14757">
                    <div className="bg-gray-50 p-4 rounded-md mb-4" data-id="element-14758">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-14759">
                        <TruckIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-14760" />
                        Shipping Information
                      </h4>
                      <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-14761">
                        <div data-id="element-14762">
                          <p className="text-gray-500" data-id="element-14763">Carrier:</p>
                          <p className="font-medium" data-id="element-14764">
                            {selectedReceipt.carrierInfo}
                          </p>
                        </div>
                        <div data-id="element-14765">
                          <p className="text-gray-500" data-id="element-14766">Tracking Number:</p>
                          <p className="font-medium" data-id="element-14767">
                            {selectedReceipt.trackingNumber}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md mb-4" data-id="element-14768">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-14769">
                        <FileTextIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-14770" />
                        Notes
                      </h4>
                      <p className="text-sm" data-id="element-14771">{selectedReceipt.notes}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md" data-id="element-14772">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-14773">
                        <FileTextIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-14774" />
                        Delivery Dockets
                      </h4>
                      {selectedReceipt.deliveryDockets && selectedReceipt.deliveryDockets.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-14775">
                          {selectedReceipt.deliveryDockets.map(docket => <li key={docket.id} className="py-2 flex justify-between" data-id="element-14776">
                              <div className="flex items-center" data-id="element-14777">
                                <FileTextIcon className="h-4 w-4 mr-2 text-gray-400" data-id="element-14778" />
                                <span className="text-sm" data-id="element-14779">{docket.name}</span>
                              </div>
                              <div className="flex items-center" data-id="element-14780">
                                <span className="text-xs text-gray-500 mr-2" data-id="element-14781">
                                  {formatDate(docket.uploadDate, locale)}
                                </span>
                                <button className="text-indigo-600 text-sm hover:text-indigo-800" data-id="element-14782">
                                  View
                                </button>
                              </div>
                            </li>)}
                        </ul> : <p className="text-sm text-gray-500 italic" data-id="element-14783">
                          No delivery dockets attached
                        </p>}
                    </div>
                  </div>
                </div>
                {/* Receipt Line Items */}
                <div className="mt-6" data-id="element-14784">
                  <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-14785">
                    <PackageIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-14786" />
                    Receipt Line Items
                  </h4>
                  <div className="overflow-x-auto border border-gray-200 rounded-md" data-id="element-14787">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-14788">
                      <thead className="bg-gray-50" data-id="element-14789">
                        <tr data-id="element-14790">
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14791">
                            PO #
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14792">
                            Item Code
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14793">
                            Description
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14794">
                            Ordered
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14795">
                            Received
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14796">
                            Remaining
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14797">
                            Unit Price
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14798">
                            Value
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-14799">
                        {getAllItems(selectedReceipt).map(item => <tr key={`${item.poId}-${item.id}`} className="hover:bg-gray-50" data-id="element-14800">
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-indigo-600" data-id="element-14801">
                              {item.poId}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-14802">
                              {item.itemCode}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-14803">
                              {item.description}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-14804">
                              {item.poQuantity} {item.uom}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-14805">
                              {item.quantity} {item.uom}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-14806">
                              {item.remainingQuantity} {item.uom}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-14807">
                              {formatCurrency(item.unitPrice, selectedReceipt.currency, locale)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-14808">
                              {formatCurrency(item.value, selectedReceipt.currency, locale)}
                            </td>
                          </tr>)}
                        <tr className="bg-gray-50" data-id="element-14809">
                          <td colSpan="7" className="px-3 py-2 text-right text-sm font-medium text-gray-900" data-id="element-14810">
                            Total Value:
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-14811">
                            {formatCurrency(selectedReceipt.totalValue, selectedReceipt.currency, locale)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* Receipt Status Indicators */}
                <div className="mt-6 flex flex-wrap gap-4" data-id="element-14812">
                  <div className="flex items-center text-sm" data-id="element-14813">
                    <div className={`flex-shrink-0 h-4 w-4 rounded-full ${selectedReceipt.status === 'Complete' ? 'bg-green-500' : 'bg-yellow-500'} mr-1.5`} data-id="element-14814"></div>
                    <p className="text-gray-700" data-id="element-14815">
                      Receipt Status:{' '}
                      <span className="font-medium" data-id="element-14816">
                        {selectedReceipt.status}
                      </span>
                    </p>
                  </div>
                  {selectedReceipt.status === 'Complete' ? <div className="flex items-center text-sm text-green-700" data-id="element-14817">
                      <CheckCircleIcon className="h-4 w-4 mr-1.5" data-id="element-14818" />
                      <p data-id="element-14819">All items received</p>
                    </div> : <div className="flex items-center text-sm text-yellow-700" data-id="element-14820">
                      <ClockIcon className="h-4 w-4 mr-1.5" data-id="element-14821" />
                      <p data-id="element-14822">Partially received - awaiting further deliveries</p>
                    </div>}
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-14823">
                <button type="button" onClick={closeDetailsModal} className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-14824">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}